/**
 * Part 11h: a book's cover as the shelf, the banner and the book window draw it (table BW, BW10–BW11): the colour the
 * writer picked, or the one its ID gives (H9) for a book whose record has none; and a cover photo, read from the book's
 * images and shown (by the library's Cover) from an object URL that is let go when the photo changes or the cover goes.
 */
import { createEffect, createSignal, onCleanup } from 'solid-js';
import { COVER_COLOURS } from '@cockatrice/layout';
import type { BookMetadata, CoverColour } from '@cockatrice/layout';
import type { ImageReference } from '@cockatrice/core';
import { coverOf } from '../shell/shelf.ts';
import type { Shell } from './shell.ts';

/** The colour at place `n` of the six, counted round. */
export function colourAt(n: number): CoverColour {
  // Destructuring's default is no branch to count: the place is always one of the six.
  const { [n % COVER_COLOURS.length]: colour = 'harbour' } = COVER_COLOURS;
  return colour;
}

/** The colour book `id` is drawn in: the picked one, else the one its ID gives (H9). */
export function coverColourOf(id: string, metadata: BookMetadata | null): CoverColour {
  return metadata?.coverColour ?? colourAt(coverOf(id));
}

/** A colour's place among the six, which `--cover-i` draws (`data-cover`). */
export function coverIndex(colour: CoverColour): number {
  return COVER_COLOURS.indexOf(colour);
}

/**
 * BW11 (PLAN.md §3, step 2): the address of the cover photo `reference` names in book `bookId`, for the library's
 * Cover; null while it loads, or when it is missing or not the one named. Each address is let go when the next comes or
 * its owner goes.
 */
export function coverPhotoUrl(shell: Shell, bookId: () => string, reference: () => ImageReference | null): () => string | null {
  const [url, setUrl] = createSignal<string | null>(null);
  let shown: string | null = null;
  const letGo = (): void => {
    if (shown !== null) shell.revokeUrl(shown);
    shown = null;
  };
  createEffect(() => {
    const id = bookId();
    const named = reference();
    letGo();
    setUrl(null);
    if (named === null) return;
    let current = true;
    onCleanup(() => {
      current = false;
    });
    void shell.workspace.coverImage(id, named).then((image) => {
      if (!current || image === null) return;
      shown = shell.fileUrl(Uint8Array.from(image.bytes), `image/${image.format}`);
      setUrl(shown);
    });
  });
  onCleanup(letGo);
  return url;
}
